/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Bar } from './Bar';
import { BarItemData, BarSeriesData, StackLabelContext } from './barChart.types';
import { getBarProps, getRectCoord } from './utils/barItemUtils';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { getBarGapsInfo } from './utils/barSeriesUtils';
import { getBaseAndEndCoord } from './utils/barLayoutUtils';
import { getMixedFreqGroupCenter } from '#utils/PRIVATE_chartUtils/layoutUtils';
import { Info, Scale, Group, ChartSeriesData } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { VisTextStyle, supportsMobileScreenReader } from '#utils/UNSAFE_visUtils';
import { Font } from '#hooks/PRIVATE_useTextDimensions';
import { StackLabels } from './BarChartStackLabel';
import { BarLabel } from './BarLabel';
import { JSX } from 'preact/jsx-runtime';

const ZERO_BAR_SIZE = 3;
type BarGroupProps<K, D> = {
  splitDualY: boolean;
  yScale: Scale;
  y2Scale: Scale;
  xScale: Scale;
  colors: ColorProps['color'][];
  series: BarSeriesData<K, D>[];
  group: Group;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
  focusedItemInfo?: Info;
  hoveredItemInfo?: Info;
  isSelectionEnabled: boolean;
  selectedIds?: Set<K>;
  hiddenIds?: Set<K>;
  highlightedIds?: Set<K>;
  groupIndex: number;
  barGapRatio: number;
  dataItemGaps: number;
  axisStepWidth: number;
  averageGroupZ: number;
  isStacked: boolean;
  activeId?: string;
  orientation: 'horizontal' | 'vertical';
  isDrillEnabled: boolean;
  isLog: boolean;
  isRtl: boolean;
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  isPointInsideMarquee: ((id: K, point: { x: number; y: number }) => boolean) | undefined;
  getTextDimensions?: (text: string, style: Font) => Dimension;
  defaultFontSize: string;
  stacklabelStyle?: VisTextStyle;
  getStackLabel?: (context: StackLabelContext<D>) => string;
  getStackGroup?: (s: ChartSeriesData<K, D>) => string | number | undefined;
  seriesIndex?: number;
  plotAreaClipPathId?: string;
  getItemAriaLabel: (
    data: D,
    seriesIndex: number,
    groupIndex: number,
    isSelectionEnabled: boolean,
    isSelected: boolean,
    isDrillable: boolean
  ) => string;
};

export function BarGroup<K extends string | number, D extends BarItemData<K>>({
  yScale,
  y2Scale,
  xScale,
  colors,
  series,
  groupIndex,
  hiddenIds,
  barGapRatio,
  dataItemGaps,
  axisStepWidth,
  averageGroupZ,
  highlightedIds,
  selectedIds,
  isPointInsideMarquee,
  orientation,
  isLog,
  activeId,
  getDataItem,
  isRtl,
  isStacked,
  isSelectionEnabled,
  focusedItemInfo,
  hoveredItemInfo,
  isDrillEnabled,
  group,
  timeAxisType,
  getTextDimensions,
  defaultFontSize,
  stacklabelStyle,
  getStackLabel,
  getStackGroup,
  getItemAriaLabel,
  splitDualY,
  seriesIndex,
  plotAreaClipPathId
}: BarGroupProps<K, D>) {
  const isHoriz = orientation === 'horizontal';
  const items: JSX.Element[] = [];
  const seriesY = series.filter((s) => s.associatedYAxis !== 'y2');
  const seriesY2 = series.filter((s) => s.associatedYAxis === 'y2');
  const labels: JSX.Element[] = [];

  const numSeriesY = seriesY.length;
  const numSeriesY2 = series.length - numSeriesY;

  // Determine unique stack groups per axis (preserving series order)
  const stackGroupOf = (s: ChartSeriesData<K, D>) =>
    (getStackGroup?.(s) ?? 'default') as string | number;
  const yStackGroups = Array.from(new Set(seriesY.map(stackGroupOf)));
  const y2StackGroups = Array.from(new Set(seriesY2.map(stackGroupOf)));
  const numStacksY = yStackGroups.length;
  const numStacksY2 = y2StackGroups.length;

  let yPosCumulativeValue = isLog ? yScale.domain()[0] : 0;
  let yNegCumulativeValue = 0;

  let y2PosCumulativeValue = isLog ? y2Scale.domain()[0] : 0;
  let y2NegCumulativeValue = 0;

  const cumuls = new Map<string, { pos: number; neg: number; center: number }>();
  const posItemsMap = new Map<string, D[]>();
  const negItemsMap = new Map<string, D[]>();

  const isStackLabelRendered = isStacked && !!getStackLabel && timeAxisType != 'mixedFrequency';

  let groupCenter =
    timeAxisType != 'mixedFrequency'
      ? xScale.transform(timeAxisType != undefined ? new Date(group.id).getTime() : groupIndex)
      : undefined;

  let isAssociatedToY2;
  const processSeries = (_seriesIndex: number) => {
    isAssociatedToY2 = series[_seriesIndex].associatedYAxis === 'y2';
    const item = getDataItem(_seriesIndex, groupIndex);
    if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
      return;
    }

    if (timeAxisType === 'mixedFrequency') {
      groupCenter = getMixedFreqGroupCenter<K, D>(xScale, item);
    }

    const isFocused =
      focusedItemInfo?.refObjId == undefined &&
      focusedItemInfo?.groupIndex === groupIndex &&
      focusedItemInfo?.seriesIndex === _seriesIndex &&
      focusedItemInfo?.isFocusVisible;
    const isHovered =
      hoveredItemInfo?.groupIndex === groupIndex &&
      hoveredItemInfo?.seriesIndex === _seriesIndex &&
      hoveredItemInfo?.isCurrent;
    const isFocusVisible = !!isFocused || (!!isHovered && isSelectionEnabled);
    const isActive = (isFocused && focusedItemInfo.isCurrent) || isHovered;
    const isHighlighted =
      highlightedIds === undefined || highlightedIds.size === 0 || highlightedIds.has(item.id);

    const isDrillable = item.drilling === 'on' || (item.drilling != 'off' && isDrillEnabled);

    const stackGroup = getStackGroup?.(series[_seriesIndex]) ?? 'default';
    const axis = isAssociatedToY2 ? 'y2' : 'y';
    const key = stackGroup + '_' + axis;
    if (!cumuls.has(key)) {
      cumuls.set(key, {
        pos: isLog ? (isAssociatedToY2 ? y2Scale : yScale).domain()[0] : 0,
        neg: 0,
        center: 0
      });
    }
    if (!posItemsMap.has(key)) {
      posItemsMap.set(key, []);
      negItemsMap.set(key, []);
    }
    const cumul = cumuls.get(key)!;
    const negCumul = isStacked
      ? cumul.neg
      : isAssociatedToY2
      ? y2NegCumulativeValue
      : yNegCumulativeValue;
    const posCumul = isStacked
      ? cumul.pos
      : isAssociatedToY2
      ? y2PosCumulativeValue
      : yPosCumulativeValue;

    // Choose index and counts for bar layout based on stack groups.
    // When there are multiple stack groups on the same axis, we treat each stack group
    // as a separate "series" for horizontal positioning, so grouped stacks render side-by-side.
    const stackGroupIndex = (axis === 'y2' ? y2StackGroups : yStackGroups).indexOf(stackGroup);
    const useGroupedStacks = isStacked && (axis === 'y2' ? numStacksY2 : numStacksY) > 1;

    const gapsSeriesIndex = useGroupedStacks
      ? stackGroupIndex
      : splitDualY
      ? (isAssociatedToY2 ? seriesY2 : seriesY).findIndex(
          (value) => value.id === series[_seriesIndex].id
        )
      : _seriesIndex;

    const gapsNumSeriesY = useGroupedStacks ? numStacksY : numSeriesY;
    const gapsNumSeriesY2 = useGroupedStacks ? numStacksY2 : numSeriesY2;
    // For grouped-stacked layout, pass isStacked=false to compute side-by-side offsets for stacks.
    const gapsIsStacked = isStacked && !useGroupedStacks;

    const { barWidth, offset, itemsGaps } = getBarGapsInfo(
      gapsSeriesIndex,
      groupIndex,
      series[_seriesIndex],
      axisStepWidth,
      averageGroupZ,
      getDataItem,
      barGapRatio,
      splitDualY
        ? gapsNumSeriesY
        : useGroupedStacks
        ? axis === 'y2'
          ? numStacksY2
          : numStacksY
        : series.length,
      splitDualY
        ? gapsNumSeriesY2
        : useGroupedStacks
        ? axis === 'y2'
          ? numStacksY2
          : numStacksY
        : series.length,
      dataItemGaps,
      gapsIsStacked,
      isRtl,
      isHoriz
    );
    const x1 = groupCenter! + offset;
    const x2 = x1 + barWidth;

    cumuls.get(key)!.center = (x1 + x2) / 2;

    const { baselineCoord, endCoord } = getBaseAndEndCoord(
      item.value,
      isAssociatedToY2 ? y2Scale : yScale,
      isStacked,
      isLog,
      itemsGaps,
      negCumul,
      posCumul
    );

    if (isStacked) {
      if (item.value < 0) {
        cumul.neg += item.value;
      } else {
        cumul.pos += item.value;
      }
    } else {
      if (isAssociatedToY2) {
        y2NegCumulativeValue += item.value < 0 ? item.value : 0;
        y2PosCumulativeValue += item.value > 0 ? item.value : 0;
      } else {
        yNegCumulativeValue += item.value < 0 ? item.value : 0;
        yPosCumulativeValue += item.value > 0 ? item.value : 0;
      }
    }

    if (isStackLabelRendered) {
      if (item.value >= 0) {
        posItemsMap.get(key)!.push(item);
      } else {
        negItemsMap.get(key)!.push(item);
      }
    }
    const barprops = getBarProps(item, series[_seriesIndex], colors?.[_seriesIndex]);
    const rectParam = getRectCoord(x1, x2, endCoord, baselineCoord, isHoriz);
    // when value is zero, the bar will be transparent and of height 3px
    // parity check with legacy, check behaivour during stacking.
    if (Math.abs(endCoord - baselineCoord) < 1) {
      barprops.fill = 'transparent';
      if (!isHoriz) {
        rectParam.height = ZERO_BAR_SIZE;
        if (rectParam.y > baselineCoord || yScale.domain()[1] <= 0) {
          // if horizontal, R2L must be considered to draw bar on positive side of baseline
          rectParam.y = baselineCoord;
        } else {
          rectParam.y = baselineCoord - ZERO_BAR_SIZE;
        }
      } else {
        rectParam.width = ZERO_BAR_SIZE;
        rectParam.x = rectParam.x - ZERO_BAR_SIZE;
      }
    }

    const isSelected = isPointInsideMarquee
      ? isPointInsideMarquee?.(item.id, {
          x: rectParam.x + rectParam.width / 2,
          y: rectParam.y
        })
      : selectedIds?.has(item.id!);

    const ariaLabel =
      supportsMobileScreenReader || isActive
        ? getItemAriaLabel(
            item,
            _seriesIndex,
            groupIndex,
            isSelectionEnabled,
            !!isSelected,
            isDrillable
          )
        : '';

    items.push(
      <Bar
        {...rectParam}
        {...barprops}
        seriesIndex={_seriesIndex}
        groupIndex={groupIndex}
        isFocused={isFocusVisible}
        activeId={isActive ? activeId : ''}
        aria-label={ariaLabel}
        isDimmed={!isHighlighted}
        isSelected={!!isSelected}
        isPointerEnabled={isDrillable || isSelectionEnabled}
        key={`${groupIndex};${_seriesIndex}`}
      />
    );
    labels.push(
      <BarLabel
        item={item}
        isRtl={isRtl}
        seriesColor={colors[_seriesIndex % 12]}
        isStacked={isStacked}
        defaultFontSize={defaultFontSize}
        barDim={rectParam}
        isHoriz={isHoriz}
        getTextDimensions={getTextDimensions}
      />
    );
  };

  if (seriesIndex !== undefined) {
    processSeries(seriesIndex);
  } else {
    series.forEach((_, index) => processSeries(index));
  }

  return (
    <>
      <g clip-path={plotAreaClipPathId}>{items}</g>
      {labels}
      {isStackLabelRendered &&
        getTextDimensions &&
        [...cumuls.keys()].map((key) => {
          const cumul = cumuls.get(key)!;
          const axis = key.split('_')[1];
          const yScaleToUse = axis === 'y2' ? y2Scale : yScale;
          return (
            <StackLabels
              key={key}
              group={group}
              isRtl={isRtl}
              yScale={yScaleToUse}
              getStackLabel={getStackLabel}
              defaultFontSize={defaultFontSize}
              getTextDimensions={getTextDimensions}
              posItemsData={posItemsMap.get(key)!}
              negItemsData={negItemsMap.get(key)!}
              isHoriz={isHoriz}
              groupCenter={cumul.center}
              labelStyle={stacklabelStyle}
              posValue={cumul.pos}
              negValue={cumul.neg}
            />
          );
        })}
    </>
  );
}
